import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectedTripSelector } from '../../../features/tripcenter/tripControlSlice';
import {
    setMapControl, setZoom, toggleVehicleIcons, setNeedBounding,
    tripLayersSelector, resetTripLayers
} from '../../../features/map/mapSlice';
import {
    clearVisibleAssetsMarkers,
    restoreVisibleAssetsMarkers,
    removeDetailView,
    addToVisbleAssetsMarkers,
    getAllVehiclesAsRack
} from '../../../features/assets/assetsSlice';
import { Polyline, Marker, InfoWindow, Polygon, OverlayView } from '@react-google-maps/api';

import { tripDateView, tripDurationFormat, tripHoursFormat } from '../../../utils/dateFormat';


const routePathOptions = {
    strokeColor: 'Gray', //'Crimson',//'#505050',
    strokeOpacity: 0.55,
    strokeWeight: 3,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1
};

const donePathOptions = {
    strokeColor: 'ForestGreen',//'#006400',
    strokeWeight: 4,
    strokeOpacity: 1,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 2,
    icons: [

        /* {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: 'Crimson',
                fillColor: 'Crimson',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '20%',
        }, */
        {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: 'Black',
                fillColor: 'Black',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '40%',
        },
       /*  {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: 'Crimson',
                fillColor: 'Crimson',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '65%',
        }, */
        {
            icon: {
                path: window.google && window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: 'Black',
                fillColor: 'Black',
                fillOpacity: 1,
                strokeWeight: 2
            },
            offset: '90%',
        }
    ],
};

const createTripPathOptions = (google) => ({
    strokeColor: 'DarkOrange',
    strokeOpacity: 1,
    strokeWeight: 3,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 3,
    icons: [
        {
            icon: {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: 'DarkOrange',
                fillColor: 'DarkOrange',
                fillOpacity: 1,
                strokeWeight: 1,
            },
            offset: '60px',
            repeat: '120px',
        }
    ]
});

const generateSvgMarker = (color, geometry, google) => {
    // SVG paths for different geometries (centered around 0,0 in a 10x10 viewport)
    const paths = {
        square: "M-5,-5 L5,-5 L5,5 L-5,5 Z",
        hexagon: "M-3,-5 L3,-5 L6,0 L3,5 L-3,5 L-6,0 Z",
        star: "M0,-5 L1.5,-1.5 L5,-1.5 L2.5,1 L3.5,5 L0,2.5 L-3.5,5 L-2.5,1 L-5,-1.5 L-1.5,-1.5 Z"
    };

    // Validate geometry parameter
    if (!paths[geometry]) {
        return {
            path: paths[geometry],
            fillColor: color,
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2,
            rotation: 0,
            scale: 2,
            // Center anchor point for all shapes
            anchor: new google.maps.Point(0, 0)
        };
    }

    return {
        path: paths[geometry],
        fillColor: color,
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2,
        rotation: 0,
        scale: 2,
        // Center anchor point for all shapes
        anchor: new google.maps.Point(0, 0)
    };
};

const GoogleTripView = ({ map }) => {
    const dispatch = useDispatch();
    const selectedTrip = useSelector(selectedTripSelector);
    const assetRack = useSelector(getAllVehiclesAsRack);
    const tripLayers = useSelector(tripLayersSelector);

    const {
        route = null,
        tripPath = [], nearestPointIndex = 0, locMeta = {},
        deviceImei = null,
        stops = []
    } = selectedTrip || {};
    const tripId = selectedTrip?._id;


    const [isStartPointInfoOpen, setIsStartPointInfoOpen] = useState(false);
    const [isEndPointInfoOpen, setIsEndPointInfoOpen] = useState(false);
    const [viaPointInfoOpen, setViaPointInfoOpen] = useState({});  // Object to track multiple via points
    const [routePointInfoOpen, setRoutePointInfoOpen] = useState(false);
    const [tripPointInfoOpen, setTripPointInfoOpen] = useState(false);
    const [stopInfoOpen, setStopInfoOpen] = useState({});  // Object to track multiple stop info windows

    // Add this new selector to get the current location from assets state


    useEffect(() => {
        if (tripId && map) {
            console.log("#GoogleTripView Mounted", selectedTrip.truckRegistrationNumber, selectedTrip.lastCheckTime, selectedTrip.lastCheckResult);
            dispatch(setMapControl('trip'));
            dispatch(resetTripLayers());
            /* console.log("remove detail view")
            dispatch(removeDetailView()); */
            dispatch(clearVisibleAssetsMarkers());
            if (deviceImei) {
                dispatch(addToVisbleAssetsMarkers(deviceImei));
            }
            dispatch(toggleVehicleIcons(true));
            dispatch(setZoom(10));
            dispatch(setNeedBounding(true));

            // Add this section to dim the map
            map.setOptions({
                styles: [
                    {
                        featureType: "all",
                        elementType: "all",
                        stylers: [
                            { saturation: -50 }, // Reduce saturation
                            { lightness: 10 }    // Slightly increase lightness
                        ]
                    }
                ]
            });
        }

        return () => {
            console.log("#GoogleTripView Unmounting Trip View, resetting map control to assets and styles");
            dispatch(setMapControl('asset'));
            dispatch(restoreVisibleAssetsMarkers());
            dispatch(toggleVehicleIcons(true));
            dispatch(setNeedBounding(false));
            dispatch(resetTripLayers());

            // Reset map styles when component unmounts
            if (map) {
                map.setOptions({
                    styles: []  // Reset to default styles
                });
            }
        }
    }, [dispatch, map, tripId, deviceImei]);

    if (!selectedTrip?.fullyLoaded) {
        //console.log("selectedTrip not fully loaded", selectedTrip);
        return null;
    }

    // GoogleTripView is imported before useJsApiLoader finishes. Build this
    // option only when the loaded Google Maps API is available.
    const tripPathOptions = createTripPathOptions(window.google);

    const routePath = route?.routePath;
    const startLocation = route?.startLocation;
    const endLocation = route?.endLocation;
    const viaLocations = route?.viaLocations;
    const routeZones = route?.routeZones;
    const routeMarkers = route?.routeMarkers;

    const donePath = routePath ? routePath.slice(0, nearestPointIndex + 1) : null;

    // Helper function to validate coordinates
    const isValidPath = (path) => {
        const valid = path?.every(point =>
            point &&
            typeof point.lat === 'number' &&
            typeof point.lng === 'number' &&
            isFinite(point.lat) &&
            isFinite(point.lng)
        );
        return valid;
    };
    // Helper function to sanitize path coordinates
    const sanitizePath = (path) => {
        if (!path) return null;
        return path.reduce((validPoints, point) => {
            const lat = parseFloat(point.lat);
            const lng = parseFloat(point.lng);
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                validPoints.push({ lat, lng });
            }
            return validPoints;
        }, []);
    };
    let tpath = [];
    if (tripPath && tripPath.length > 0) {
        tripPath.forEach(point => {
            tpath.push({
                lat: parseFloat(point.coordinates[1]),
                lng: parseFloat(point.coordinates[0])
            });
        });
    }
    // Sanitize all paths
    const sanitizedRoutePath = sanitizePath(routePath);
    const sanitizedTripPath = sanitizePath(tpath);
    const sanitizedDonePath = sanitizePath(donePath);

    const sanitizedStartLocation = startLocation ? {
        point: {
            lat: parseFloat(startLocation.lat),
            lng: parseFloat(startLocation.lng),
        },
        locationName: startLocation.locationName,
        address: startLocation.address,
        locationType: startLocation.locationType,
        zoneCoordinates: startLocation.zoneCoordinates.map(coordinate => ({
            lat: parseFloat(coordinate[1]),
            lng: parseFloat(coordinate[0])
        }))
    } : null;
    const sanitizedEndLocation = endLocation ? {
        point: {
            lat: parseFloat(endLocation.lat),
            lng: parseFloat(endLocation.lng),
        },
        locationName: endLocation.locationName,
        address: endLocation.address,
        locationType: endLocation.locationType,
        zoneCoordinates: endLocation.zoneCoordinates.map(coordinate => ({
            lat: parseFloat(coordinate[1]),
            lng: parseFloat(coordinate[0])
        }))
    } : null;
    const sanitizedViaLocations = viaLocations ? viaLocations.map(location => ({
        point: {
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lng),
        },
        locationName: location.locationName,
        address: location.address,
        locationType: location.locationType,
        zoneCoordinates: location.zoneCoordinates.map(coordinate => ({
            lat: parseFloat(coordinate[1]),
            lng: parseFloat(coordinate[0])
        }))
    })) : null;
    const sanitizedRouteZones = routeZones ? routeZones.map(zone => ({
        locationName: zone.zone_name,
        zoneColor: zone.zone_color,
        zoneVisible: zone.zone_visible,
        zoneNameVisible: zone.zone_name_visible,
        zoneCoords: zone.zone_coords.map(coord => ({
            lat: parseFloat(coord.lat),
            lng: parseFloat(coord.lng)
        })).filter(coord => !isNaN(coord.lat) && !isNaN(coord.lng)),
        zoneTags: zone.zone_tags
    })).filter(zone => zone.zoneCoords.length >= 3) : null;
    //console.log("sanitizedRouteZones", sanitizedRouteZones);
    //console.log("First zone coords:", sanitizedRouteZones?.[0]?.zoneCoords);
    const sanitizedRouteMarkers = routeMarkers ? routeMarkers.map(marker => ({
        point: {
            lat: parseFloat(marker.lat),
            lng: parseFloat(marker.lng),
        }
    })) : null;
    // Get current location from redux if deviceImei is available
    const currentVehicleLocation = deviceImei && assetRack[deviceImei]?.coreData ? {
        lat: parseFloat(assetRack[deviceImei].coreData.lat),
        lng: parseFloat(assetRack[deviceImei].coreData.lng)
    } : null;

    if (!selectedTrip) {
        return null;
    } else {
        //console.log("selectedTrip", donePath?.length, selectedTrip);
        return (
            <Fragment>
                <Fragment>
                {tripLayers.plannedRoute && sanitizedRoutePath && sanitizedRoutePath.length > 3 && isValidPath(sanitizedRoutePath) && (
                    <Polyline
                        path={sanitizedRoutePath}
                        options={routePathOptions}
                    />
                )}
                {
                    tripLayers.truckPath && sanitizedTripPath && sanitizedTripPath.length > 3 && isValidPath(sanitizedTripPath) && (
                        <Polyline
                            path={sanitizedTripPath}
                            options={tripPathOptions}
                        />
                    )
                }
                {tripLayers.routeProgress && sanitizedDonePath && sanitizedDonePath.length > 3 && isValidPath(sanitizedDonePath) && (
                    <Polyline
                        path={sanitizedDonePath}
                        options={donePathOptions}
                    />
                )}
                </Fragment>
                {tripLayers.routePlaces && sanitizedStartLocation && (
                    <Marker
                        icon={generateSvgMarker("DodgerBlue", "hexagon", window.google)}
                        position={sanitizedStartLocation.point}
                        onClick={() => setIsStartPointInfoOpen(true)}
                    >
                        {isStartPointInfoOpen && (
                            <InfoWindow
                                position={sanitizedStartLocation.point}
                                onCloseClick={() => setIsStartPointInfoOpen(false)}
                            >
                                <div style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    maxWidth: '300px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0',
                                        color: '#1a237e',
                                        borderBottom: '2px solid #e0e0e0',
                                        paddingBottom: '8px'
                                    }}>Start: {sanitizedStartLocation.locationName}</h3>

                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {sanitizedStartLocation.address !== sanitizedStartLocation.locationName && (
                                            <div style={{ color: '#666', marginBottom: '8px' }}>{sanitizedStartLocation.address}</div>
                                        )}

                                        {locMeta?.startLocation?.entryTime && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#e3f2fd',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#1565c0',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Entry</span>
                                                <span>{tripDateView(locMeta?.startLocation?.entryTime)}</span>
                                            </div>
                                        )}

                                        {locMeta?.startLocation?.dwellTime && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#e8f5e9',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#2e7d32',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Wait</span>
                                                <span>{tripHoursFormat(locMeta?.startLocation?.dwellTime)}</span>
                                            </div>
                                        )}

                                        {locMeta?.startLocation?.exitTime && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#fff3e0',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#e65100',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Exit</span>
                                                <span>{tripDateView(locMeta?.startLocation?.exitTime)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                )}
                {tripLayers.routePlaces && sanitizedStartLocation && sanitizedStartLocation.zoneCoordinates && (
                    <Polygon
                        paths={sanitizedStartLocation.zoneCoordinates}
                        options={{
                            fillColor: "DodgerBlue",
                            fillOpacity: 0.35,
                            strokeColor: "white",
                            strokeWeight: 2
                        }}
                    />
                )}

                {tripLayers.routePlaces && sanitizedEndLocation && (
                    <Marker
                        icon={generateSvgMarker("Navy", "hexagon", window.google)}
                        position={sanitizedEndLocation.point}
                        onClick={() => setIsEndPointInfoOpen(true)}
                    >
                        {isEndPointInfoOpen && (
                            <InfoWindow
                                position={sanitizedEndLocation.point}
                                onCloseClick={() => setIsEndPointInfoOpen(false)}
                            >
                                <div style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    maxWidth: '300px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0',
                                        color: '#1a237e',
                                        borderBottom: '2px solid #e0e0e0',
                                        paddingBottom: '8px'
                                    }}>End: {sanitizedEndLocation.locationName}</h3>

                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {sanitizedEndLocation.address !== sanitizedEndLocation.locationName && (
                                            <div style={{ color: '#666', marginBottom: '8px' }}>{sanitizedEndLocation.address}</div>
                                        )}

                                        {locMeta?.endLocation?.entryTime && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#e3f2fd',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#1565c0',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Entry</span>
                                                <span>{tripDateView(locMeta?.endLocation?.entryTime)}</span>
                                            </div>
                                        )}

                                        {locMeta?.endLocation?.dwellTime && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#e8f5e9',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#2e7d32',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Wait</span>
                                                <span>{tripHoursFormat(locMeta?.endLocation?.dwellTime)}</span>
                                            </div>
                                        )}

                                        {locMeta?.endLocation?.exitTime && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#fff3e0',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#e65100',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Exit</span>
                                                <span>{tripDateView(locMeta?.endLocation?.exitTime)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                )}
                {tripLayers.routePlaces && sanitizedEndLocation && sanitizedEndLocation.zoneCoordinates && (
                    <Polygon
                        paths={sanitizedEndLocation.zoneCoordinates}
                        options={{
                            fillColor: "Navy",
                            fillOpacity: 0.35,
                            strokeColor: "white",
                            strokeWeight: 2
                        }}
                    />
                )}

                {tripLayers.routePlaces && sanitizedViaLocations && sanitizedViaLocations.length > 0 && sanitizedViaLocations.map((sanitizedViaLocation, index) => (
                    <Fragment key={index}>
                        <Marker
                            icon={generateSvgMarker("Orchid", "square", window.google)}
                            position={sanitizedViaLocation.point}
                            onClick={() => setViaPointInfoOpen(prev => ({ ...prev, [index]: true }))}
                        >
                            {viaPointInfoOpen[index] && (
                                <InfoWindow
                                    position={sanitizedViaLocation.point}
                                    onCloseClick={() => setViaPointInfoOpen(prev => ({ ...prev, [index]: false }))}
                                >
                                    <div style={{
                                        padding: '8px',
                                        borderRadius: '8px',
                                        maxWidth: '300px',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                    }}>
                                        <h3 style={{
                                            margin: '0 0 12px 0',
                                            color: '#1a237e',
                                            borderBottom: '2px solid #e0e0e0',
                                            paddingBottom: '8px'
                                        }}>Stop {index + 1}: {sanitizedViaLocation.locationName}</h3>

                                        <div style={{ display: 'grid', gap: '8px' }}>
                                            {sanitizedViaLocation.address !== sanitizedViaLocation.locationName && (
                                                <div style={{ color: '#666', marginBottom: '8px' }}>{sanitizedViaLocation.address}</div>
                                            )}

                                            {locMeta?.[`viaLocation${index + 1}`]?.entryTime && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        backgroundColor: '#e3f2fd',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        color: '#1565c0',
                                                        fontWeight: 'bold',
                                                        minWidth: '100px'
                                                    }}>Entry</span>
                                                    <span>{tripDateView(locMeta?.[`viaLocation${index + 1}`]?.entryTime)}</span>
                                                </div>
                                            )}

                                            {locMeta?.[`viaLocation${index + 1}`]?.dwellTime && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        backgroundColor: '#e8f5e9',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        color: '#2e7d32',
                                                        fontWeight: 'bold',
                                                        minWidth: '100px'
                                                    }}>Wait</span>
                                                    <span>{tripHoursFormat(locMeta?.[`viaLocation${index + 1}`]?.dwellTime)}</span>
                                                </div>
                                            )}

                                            {locMeta?.[`viaLocation${index + 1}`]?.exitTime && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        backgroundColor: '#fff3e0',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        color: '#e65100',
                                                        fontWeight: 'bold',
                                                        minWidth: '100px'
                                                    }}>Exit</span>
                                                    <span>{tripDateView(locMeta?.[`viaLocation${index + 1}`]?.exitTime)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </InfoWindow>
                            )}
                        </Marker>
                        {sanitizedViaLocation.zoneCoordinates && (
                            <Polygon
                                paths={sanitizedViaLocation.zoneCoordinates}
                                options={{
                                    fillColor: "Orchid",
                                    fillOpacity: 0.35,
                                    strokeColor: "white",
                                    strokeWeight: 2
                                }}
                            />
                        )}
                    </Fragment>
                ))}

                {tripLayers.routeProgress && sanitizedRoutePath && sanitizedRoutePath.length > 0 && nearestPointIndex >= 0 && (
                    <Marker
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: "SeaGreen",
                            fillOpacity: 1,
                            scale: 8,
                            strokeWeight: 2,
                            strokeColor: "White"
                        }}
                        position={sanitizedRoutePath[nearestPointIndex]}
                        onClick={() => setRoutePointInfoOpen(true)}
                    >
                        {routePointInfoOpen && (
                            <InfoWindow
                                position={sanitizedRoutePath[nearestPointIndex]}
                                onCloseClick={() => setRoutePointInfoOpen(false)}
                            >
                                <div style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    maxWidth: '300px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0',
                                        color: '#1a237e',
                                        borderBottom: '2px solid #e0e0e0',
                                        paddingBottom: '8px'
                                    }}>{selectedTrip?.route?.routeName}</h3>

                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                backgroundColor: '#e3f2fd',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                color: '#1565c0',
                                                fontWeight: 'bold',
                                                minWidth: '100px'
                                            }}>Covered</span>
                                            <span>{selectedTrip?.distanceCovered?.toFixed(1)} km</span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                backgroundColor: '#e8f5e9',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                color: '#2e7d32',
                                                fontWeight: 'bold',
                                                minWidth: '100px'
                                            }}>Remaining</span>
                                            <span>{selectedTrip?.distanceRemaining?.toFixed(1)} km</span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                backgroundColor: '#fff3e0',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                color: '#e65100',
                                                fontWeight: 'bold',
                                                minWidth: '100px'
                                            }}>Progress</span>
                                            <span>{selectedTrip?.completionPercentage?.toFixed(1)}%</span>
                                        </div>
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                )}
                {tripLayers.truckPath && sanitizedTripPath && sanitizedTripPath.length > 0 && (
                    <Marker
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: "DarkOrange",
                            fillOpacity: 1,
                            scale: 8,
                            strokeWeight: 2,
                            strokeColor: "White"
                        }}
                        position={sanitizedTripPath[sanitizedTripPath.length - 1]}
                        onClick={() => setTripPointInfoOpen(true)}
                    >
                        {tripPointInfoOpen && (
                            <InfoWindow
                                position={sanitizedTripPath[sanitizedTripPath.length - 1]}
                                onCloseClick={() => setTripPointInfoOpen(false)}
                            >
                                <div style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    maxWidth: '300px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 12px 0',
                                        color: '#1a237e',
                                        borderBottom: '2px solid #e0e0e0',
                                        paddingBottom: '8px'
                                    }}>{selectedTrip?.truckRegistrationNumber}</h3>

                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                backgroundColor: '#e3f2fd',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                color: '#1565c0',
                                                fontWeight: 'bold',
                                                minWidth: '100px'
                                            }}>Truck Run</span>
                                            <span>{selectedTrip?.truckRunDistance?.toFixed(1)} km</span>
                                        </div>

                                        {selectedTrip?.runDuration && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#e8f5e9',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#2e7d32',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Drive Time</span>
                                                <span>{tripHoursFormat(selectedTrip?.runDuration)}</span>
                                            </div>
                                        )}

                                        {selectedTrip?.parkedDuration && selectedTrip?.parkedDuration > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    backgroundColor: '#fff3e0',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    color: '#e65100',
                                                    fontWeight: 'bold',
                                                    minWidth: '100px'
                                                }}>Mileage</span>
                                                <span>{selectedTrip?.fuelEfficiency} Km/L</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                )}

                {/* Add stop markers */}
                {tripLayers.stops && stops && stops.length > 0 && stops.map((stop, index) => {
                    const stopPosition = {
                        lat: parseFloat(stop.startCoordinates.lat),
                        lng: parseFloat(stop.startCoordinates.lng)
                    };

                    // Validate coordinates
                    if (!isFinite(stopPosition.lat) || !isFinite(stopPosition.lng)) {
                        return null;
                    }

                    return (
                        <Fragment key={`stop-${index}`}>
                            <Marker
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    fillColor: "DarkMagenta",
                                    fillOpacity: 1,
                                    scale: 10,
                                    strokeWeight: 3,
                                    strokeColor: "white"
                                }}
                                label={{
                                    text: 'P',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    scale: 8
                                }}
                                position={stopPosition}
                                zIndex={500}
                                onClick={() => setStopInfoOpen(prev => ({ ...prev, [index]: true }))}
                            >
                                {stopInfoOpen[index] && (
                                    <InfoWindow
                                        position={stopPosition}
                                        onCloseClick={() => setStopInfoOpen(prev => ({ ...prev, [index]: false }))}
                                    >
                                        <div style={{
                                            padding: '8px',
                                            borderRadius: '8px',
                                            maxWidth: '300px',
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                        }}>
                                            <h3 style={{
                                                margin: '0 0 12px 0',
                                                color: '#1a237e',
                                                borderBottom: '2px solid #e0e0e0',
                                                paddingBottom: '8px'
                                            }}>Stop {index + 1}</h3>

                                            <div style={{ display: 'grid', gap: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        backgroundColor: '#e3f2fd',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        color: '#1565c0',
                                                        fontWeight: 'bold',
                                                        minWidth: '100px'
                                                    }}>Start</span>
                                                    <span>{tripDateView(stop.stopStartTime)}</span>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        backgroundColor: '#fff3e0',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        color: '#e65100',
                                                        fontWeight: 'bold',
                                                        minWidth: '100px'
                                                    }}>End</span>
                                                    <span>{tripDateView(stop.stopEndTime)}</span>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        backgroundColor: '#e8f5e9',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        color: '#2e7d32',
                                                        fontWeight: 'bold',
                                                        minWidth: '100px'
                                                    }}>Duration</span>
                                                    <span>{stop.stopDuration}</span>
                                                </div>

                                                {stop.startAddress && (
                                                    <div style={{
                                                        marginTop: '8px',
                                                        padding: '8px',
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        color: '#666'
                                                    }}>
                                                        <strong>Location:</strong><br />
                                                        {stop.startAddress.net_addr || stop.startAddress.locn || stop.startAddress.road}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </InfoWindow>
                                )}
                            </Marker>
                        </Fragment>
                    );
                })}

                {/* Add the current location marker if available */}
                {currentVehicleLocation && isFinite(currentVehicleLocation.lat) && isFinite(currentVehicleLocation.lng) && (
                    <>
                        {/* Outer circle */}
                        <Marker
                            icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                fillColor: "transparent",
                                fillOpacity: 0,
                                scale: 14,
                                strokeWeight: 3,
                                strokeColor: "red"
                            }}
                            position={currentVehicleLocation}
                            zIndex={1000}
                            clickable={false}
                        />
                        {/* Inner solid circle */}
                        <Marker
                            icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                fillColor: "red",
                                fillOpacity: 1,
                                scale: 8,
                                strokeWeight: 1,
                                strokeColor: "white"
                            }}
                            position={currentVehicleLocation}
                            zIndex={1001}
                            onClick={() => {
                                // You could add an action here if you want the marker to be clickable
                            }}
                        />
                    </>
                )}
                {tripLayers.routeAnnotations && sanitizedRouteZones && sanitizedRouteZones.length > 0 && sanitizedRouteZones.map((sanitizedRouteZone, index) => {
                    //console.log(`Rendering zone ${index}:`, sanitizedRouteZone.zoneCoords);
                    // Ensure we have valid coordinates for a polygon (at least 3 points)
                    if (!sanitizedRouteZone.zoneCoords || sanitizedRouteZone.zoneCoords.length < 3) {
                        console.warn(`Zone ${index} has insufficient coordinates:`, sanitizedRouteZone.zoneCoords);
                        return null;
                    }

                    // Calculate center point for label
                    const centerLat = sanitizedRouteZone.zoneCoords.reduce((sum, coord) => sum + coord.lat, 0) / sanitizedRouteZone.zoneCoords.length;
                    const centerLng = sanitizedRouteZone.zoneCoords.reduce((sum, coord) => sum + coord.lng, 0) / sanitizedRouteZone.zoneCoords.length;

                    // Helper function to determine if a color is bright or dark
                    const isColorBright = (hexColor) => {
                        if (!hexColor || typeof hexColor !== 'string') return false;
                        let hex = hexColor.replace('#', '');
                        if (hex.length === 3) {
                            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
                        }
                        const r = parseInt(hex.substring(0, 2), 16);
                        const g = parseInt(hex.substring(2, 4), 16);
                        const b = parseInt(hex.substring(4, 6), 16);
                        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                        return luminance > 0.6;
                    };

                    return (
                        <Fragment key={index}>
                            <Polygon
                                paths={sanitizedRouteZone.zoneCoords}
                                options={{
                                    fillColor: sanitizedRouteZone.zoneColor || "#CF3827",
                                    fillOpacity: 0.3,
                                    strokeColor: sanitizedRouteZone.zoneColor || "#CF3827",
                                    strokeOpacity: 1,
                                    strokeWeight: 2,
                                    clickable: true,
                                    draggable: false,
                                    editable: false,
                                    geodesic: false,
                                    zIndex: 1
                                }}
                            />
                            <Marker
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    fillColor: 'yellow',
                                    fillOpacity: 0.75,
                                    strokeColor: 'red',
                                    strokeWeight: 2,
                                    scale: 8
                                }}
                                position={{ lat: centerLat, lng: centerLng }}
                                title={sanitizedRouteZone.locationName}
                            />
                            <OverlayView
                                position={{ lat: centerLat, lng: centerLng }}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                                <div
                                    style={{
                                        backgroundColor: `${sanitizedRouteZone.zoneColor || '#CF3827'}DD`,
                                        position: 'absolute',
                                        left: 10,
                                        top: -16,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: isColorBright(sanitizedRouteZone.zoneColor || '#CF3827') ? '#000' : '#fff',
                                        border: `2px solid ${sanitizedRouteZone.zoneColor || '#CF3827'}`,
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        transform: 'translateX(-50%)',
                                        textAlign: 'center',
                                    }}>
                                    {sanitizedRouteZone.locationName}
                                </div>
                            </OverlayView>
                        </Fragment>
                    );
                })}
                {tripLayers.routeAnnotations && sanitizedRouteMarkers && sanitizedRouteMarkers.length > 0 && sanitizedRouteMarkers.map((sanitizedRouteMarker, index) => (
                    <Marker
                        icon={generateSvgMarker("Orchid", "square", window.google)}
                        position={sanitizedRouteMarker.point}
                    />
                ))}
            </Fragment>
        );
    }


};

export default GoogleTripView;
